import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NowCompactProductCard } from '../now/NowCompactProductCard';
import { ShoppingCartIcon, MinusIcon, PlusIcon, ClockIcon, XIcon, HeartIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { GlobalBadge } from './GlobalBadge';
import { SmartImage } from './SmartImage';
import { haptics } from '../../lib/utils/haptics';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { useHorizontalScroll } from '../../lib/hooks/useHorizontalScroll';
interface EnhancedProductCardProps {
  product: {
    id: number;
    name: string;
    brand?: string;
    price: number;
    originalPrice?: number;
    discount?: number;
    rating?: number;
    reviewCount?: number;
    image: string;
    isBestSeller?: boolean;
    isNew?: boolean;
    expressDelivery?: boolean;
    isGlobal?: boolean;
    endingSoon?: boolean;
  };
}
export function EnhancedProductCard({
  product
}: EnhancedProductCardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowDealsContext = window.location.pathname === '/offers' && searchParams.get('from') === 'now';
  const {
    cart,
    addToCart,
    updateQuantity,
    toggleWishlist,
    isInWishlist
  } = useStore();
  const cartItem = cart.find(item => item.id === product.id);
  const quantity = cartItem?.quantity || 0;
  const isWishlisted = isInWishlist(product.id);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const displayDiscount = product.discount || (product.originalPrice ? Math.round((product.originalPrice - product.price) / product.originalPrice * 100) : null);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.success();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
    toast.custom(t => <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} className="bg-white border-2 border-[#00A651] rounded-2xl p-4 shadow-2xl flex items-center gap-4 min-w-[320px]" data-id="element-5624" data-mp="ybfquh0sqk3x">
          <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-5625" data-mp="fctmmt9zgcvs">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12
        }} data-id="element-5626" data-mp="op4prir85e2">
              <ShoppingCartIcon className="w-6 h-6 text-[#00A651]" data-id="element-5627" data-mp="3rsdrl56dbse7" />
            </motion.div>
          </div>
          <div className="flex-1" data-id="element-5628" data-mp="395xx0nfk4cxh">
            <p className="text-sm font-bold text-gray-900" data-id="element-5629" data-mp="396zillu78htg">Added to Cart!</p>
            <p className="text-xs text-gray-500 line-clamp-1" data-id="element-5630" data-mp="347wcrz6ea7kf">{product.name}</p>
          </div>
          <button onClick={() => {
        toast.dismiss(t);
        navigate('/cart');
      }} className="bg-[#1E2B55] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-5631" data-mp="3i7e5uqqq46zl">
            View Cart
          </button>
        </motion.div>, {
      duration: 3000
    });
  };
  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity + 1);
  };
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity - 1);
  };
  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.selection();
    toggleWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
  };
  const handleCardClick = () => {
    haptics.impact();
    if (isMobile) {
      setShowDetailsSheet(true);
    } else {
      navigate(`/product/${product.id}`);
    }
  };
  if (isNowDealsContext) {
    return <NowCompactProductCard product={product} data-id="element-5632" data-mp="at9rszxfr6s2" />;
  }
  return <>
      <div onClick={handleCardClick} className="bg-white rounded-lg overflow-hidden active:scale-[0.98] transition-all group cursor-pointer h-full flex flex-col border border-gray-100" data-id="element-5633" data-mp="av30slviu338">
        {/* Product Image */}
        <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden flex-shrink-0" data-id="element-5634" data-mp="otkhhzn0efbn">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-5635" data-mp="1v9dc5p0xhbcq" />

          {/* Ending Soon Badge */}
          {product.endingSoon && <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-1 rounded-md flex items-center gap-1 whitespace-nowrap z-10" data-id="element-5636" data-mp="3ksbittbg103w">
              <ClockIcon className="w-3 h-3 animate-pulse" data-id="element-5637" data-mp="3ay2wjnd0a3e6" />
              ENDING SOON
            </div>}

          {/* Top-left: discount / best seller / new */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10" data-id="element-5638" data-mp="32128xixrg9bs">
            {product.discount && <div className="bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5639" data-mp="2s1l5l85ze7iz">
                -{product.discount}%
              </div>}
            {product.isBestSeller && <div className="bg-yellow-50 text-yellow-700 border border-yellow-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5640" data-mp="qagcs8sbb0x2">
                Best Seller
              </div>}
            {product.isNew && <div className="bg-green-50 text-green-700 border border-green-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5641" data-mp="2fpr20canptpv">
                New
              </div>}
          </div>

          {/* Floating Wishlist Button */}
          <button onClick={handleWishlist} className="absolute top-2 right-2 w-8 h-8 rounded-md bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors border border-gray-100 z-10" data-id="element-5642" data-mp="2arzcfoxhijou">
            <HeartIcon className={`w-4 h-4 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-5643" data-mp="33flj5620e4fx" />
          </button>
        </div>
        {/* Product Info */}
        <div className="p-2 flex flex-col flex-grow" data-id="element-5644" data-mp="2thfeigotik7d">
          {/* Top Row: Rating & Add to Cart */}
          <div className="flex items-center justify-between mb-2 min-h-[32px]" data-id="element-5645" data-mp="1e16sgg7fk7hs">
            {/* Rating */}
            <div className="flex items-center gap-1 text-gray-400 text-[10px]" data-id="element-5646" data-mp="1i54squ8hrocf">
              {product.rating !== undefined && product.reviewCount !== undefined && product.reviewCount > 0 ? <>
                  <span className="font-bold text-gray-600" data-id="element-5647" data-mp="1khynudib80dh">
                    {product.rating}
                  </span>
                  <StarIcon className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" data-id="element-5648" data-mp="1fivsxlw1naj8" />
                  <span className="opacity-80" data-id="element-5649" data-mp="rt1ifaz75y25">({product.reviewCount})</span>
                </> : <span className="opacity-0" data-id="element-5650" data-mp="mvj578e5cwoc">0.0</span>}
            </div>

            {/* Add to Cart Button */}
            <div className="relative" data-id="element-5651" data-mp="11kxekghq3e3q">
              <AnimatePresence mode="wait" data-id="element-5652" data-mp="wsnqz742yq4m">
                {quantity === 0 ? <motion.button key="add-to-cart" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} whileTap={{
                scale: 0.95
              }} onClick={handleAddToCart} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-700 hover:border-[#1E2B55] hover:text-[#1E2B55] transition-colors" data-id="element-5653" data-mp="wqt8dzga7a3p">
                    <div className="relative" data-id="element-5654" data-mp="6h1lt2k7y710">
                      <ShoppingCartIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-5655" data-mp="b9l0y3yk1a7x" />
                      <PlusIcon className="w-2.5 h-2.5 absolute -bottom-1 -right-1 bg-white rounded-sm text-gray-900" strokeWidth={2.5} data-id="element-5656" data-mp="bbp24e52jv4i" />
                    </div>
                  </motion.button> : <motion.div key="quantity-stepper" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="h-8 bg-[#1E2B55] text-white rounded-lg flex items-center justify-between overflow-hidden shadow-sm px-1" onClick={e => e.stopPropagation()} data-id="element-5657" data-mp="p8ucgqbtfg5k">
                    <button onClick={handleDecrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Decrease quantity" data-id="element-5658" data-mp="2ogkl7vl3v5ld">
                      <MinusIcon className="w-3 h-3" data-id="element-5659" data-mp="3c6om3v6wsr8o" />
                    </button>
                    <motion.span key={quantity} initial={{
                  scale: 1.2,
                  fontWeight: 700
                }} animate={{
                  scale: 1,
                  fontWeight: 700
                }} className="text-xs w-4 text-center" data-id="element-5660" data-mp="37en3zsn286aq">
                      {quantity}
                    </motion.span>
                    <button onClick={handleIncrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Increase quantity" data-id="element-5661" data-mp="1b0kopbljkah3">
                      <PlusIcon className="w-3 h-3" data-id="element-5662" data-mp="1ksgm0kfszwk9" />
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Brand */}
          {product.brand && <p className="text-[10px] text-gray-400 mb-0.5 font-medium" data-id="element-5663" data-mp="1ota9xdt8xbd5">
              {product.brand}
            </p>}
          {/* Product Name */}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 leading-tight group-hover:text-[#1E2B55] transition-colors flex-grow" data-id="element-5664" data-mp="2dd3zhqkgs45m">
            {product.name}
          </h3>
          
          {/* Price */}
          <div className="flex flex-col mt-auto" data-id="element-5665" data-mp="23e6akmj4m8m3">
            {/* Old Price & Discount */}
            <div className="flex items-center gap-2 min-h-[18px] mb-0.5" data-id="element-5666" data-mp="1pn1ajjbujeb8">
              {product.originalPrice && <>
                  <span className="text-[10px] text-gray-400 line-through" data-id="element-5667" data-mp="1ufuew63ekkms">
                    {product.originalPrice} SAR
                  </span>
                  {displayDiscount && <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-md" data-id="element-5668" data-mp="1kozoqqn1d0w2">
                      -{displayDiscount}%
                    </span>}
                </>}
            </div>
            {/* Final Price */}
            <span className={`text-base font-bold ${product.originalPrice ? 'text-red-500' : 'text-gray-900'}`} data-id="element-5669" data-mp="3ddzac6cvxn2f">
              {product.price} SAR
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Details Bottom Sheet */}
      <AnimatePresence data-id="element-5670" data-mp="2zlsrudm9f3zt">
        {showDetailsSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={e => {
          e.stopPropagation();
          setShowDetailsSheet(false);
        }} className="fixed inset-0 bg-black/50 z-[100] md:hidden" data-id="element-5671" data-mp="2zjpu17ev86kj" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] md:hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-5672" data-mp="2bw88htm724q0">
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0" data-id="element-5673" data-mp="108c29wxrbd49">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-5674" data-mp="154sqer4yhrhc" />
              </div>

              <div className="flex justify-between items-center px-4 pb-2 border-b border-gray-100 flex-shrink-0" data-id="element-5675" data-mp="1a345phe0su6y">
                <h3 className="font-bold text-lg" data-id="element-5676" data-mp="1f49ux58h1zbq">Deal Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-5677" data-mp="1j427ru4wermw">
                  <XIcon className="w-5 h-5" data-id="element-5678" data-mp="3kw6w6vn6v6sv" />
                </button>
              </div>

              <div className="overflow-y-auto p-4 flex-grow" data-id="element-5679" data-mp="2pma1c6f4r3sg">
                <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-4 relative" data-id="element-5680" data-mp="2pkpb2szum70e">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-5681" data-mp="2lkf35ashx6iw" />
                  {product.discount && <div className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-5682" data-mp="2ujk3mmnmb9xp">
                      -{product.discount}%
                    </div>}
                </div>

                {product.brand && <p className="text-sm text-gray-500 uppercase tracking-wide mb-1" data-id="element-5683" data-mp="2gn6mjemzxajr">
                    {product.brand}
                  </p>}
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5684" data-mp="1o4rje5izbr0q">
                  {product.name}
                </h2>

                <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-100" data-id="element-5685" data-mp="1j5ew5jo74hxm">
                  <div className="flex justify-between items-end" data-id="element-5686" data-mp="3usid8r10f4nb">
                    <div data-id="element-5687" data-mp="3em29hvd3412">
                      <p className="text-sm text-red-600 font-medium mb-1" data-id="element-5688" data-mp="3fxhcdu9by7h">
                        Special Deal Price
                      </p>
                      <div className="flex items-baseline gap-2" data-id="element-5689" data-mp="3lqscuvp5gvde">
                        <span className="text-3xl font-bold text-red-600" data-id="element-5690" data-mp="3pv9v2qfv30m2">
                          {product.price}
                        </span>
                        <span className="text-red-600 font-medium" data-id="element-5691" data-mp="mdrladgj3jj0">SAR</span>
                      </div>
                    </div>
                    {product.originalPrice && <div className="text-right" data-id="element-5692" data-mp="8g32db60umgl">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5693" data-mp="dca8p52bxzz2">
                          Regular Price
                        </p>
                        <span className="text-lg text-gray-400 line-through" data-id="element-5694" data-mp="2t2wfrpvo51g3">
                          {product.originalPrice} SAR
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5695" data-mp="ynhcs43svs0r">
                  <button onClick={() => {
                navigate(`/product/${product.id}`);
                setShowDetailsSheet(false);
              }} className="w-full bg-gray-100 text-gray-900 font-bold py-3.5 rounded-xl" data-id="element-5696" data-mp="yor9aov08s5a">
                    View Full Product Details
                  </button>

                  <button onClick={e => {
                handleAddToCart(e);
                setShowDetailsSheet(false);
              }} className={`w-full ${quantity > 0 ? 'bg-[#00A651]' : 'bg-[#1E2B55]'} text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg ${quantity > 0 ? 'shadow-green-900/20' : 'shadow-blue-900/20'}`} data-id="element-5697" data-mp="fwq5d83dibp0">
                    <ShoppingCartIcon className="w-5 h-5" data-id="element-5698" data-mp="218zzorg4cxwt" />
                    {quantity > 0 ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}